/**
 * Shared triage configuration — single source of truth for colors, labels,
 * and sort order. Used by live-triage.tsx, past-events.tsx, and any future
 * triage-aware UI.
 */

export interface TriageConfig {
  order: number;   // sort priority — T1 (most critical) = 0
  color: string;   // hex color for badges and map markers
  label: string;   // display label
}

export const TRIAGE_CONFIG: Record<string, TriageConfig> = {
  T1: { order: 0, color: "#c0392b", label: "T1 · Immediate" },
  T2: { order: 1, color: "#e67e22", label: "T2 · Delayed"   },
  T3: { order: 2, color: "#27ae60", label: "T3 · Minimal"   },
  T4: { order: 3, color: "#8e44ad", label: "T4 · Expectant" },
  T0: { order: 4, color: "#95a5a6", label: "T0 · Deceased"  },
};

/** Human-readable label for a triage level, e.g. "T1 · Immediate". */
export function triageLabel(level: string | null | undefined): string {
  return level ? (TRIAGE_CONFIG[level]?.label ?? "OK") : "OK";
}

/** Sort key — lower = higher priority. Unknown/null sorts last. */
export function triageSortOrder(level: string | null | undefined): number {
  return level ? (TRIAGE_CONFIG[level]?.order ?? 5) : 5;
}

/** Hex color for a triage level. Returns `fallback` when level is null/unknown. */
export function triageColorHex(level: string | null | undefined, fallback = "#666666"): string {
  if (!level) return fallback;
  return TRIAGE_CONFIG[level]?.color ?? fallback;
}

/**
 * Format vitals as a compact "HR 72 · SpO₂ 98% · Bat 85%" string.
 * Omits any field that is null/undefined.
 */
export function formatVitals(
  hrBpm?: number | null,
  spo2Pct?: number | null,
  batteryPct?: number | null,
): string {
  const parts: string[] = [];
  if (hrBpm     != null) parts.push(`HR ${hrBpm}`);
  if (spo2Pct   != null) parts.push(`SpO₂ ${spo2Pct}%`);
  if (batteryPct != null) parts.push(`Bat ${batteryPct}%`);
  return parts.join(" · ");
}
